import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { useTheme } from "@mui/system";

import {
  GraphContainer,
  HighchartsBase,
  HighchartsBaseRef,
  withErrorBoundary,
} from "@utilifeed/components";
import { useGraphColors } from "@utilifeed/core";
import { formatNumberForLocale, numbersOnly } from "@utilifeed/core/utils";
import { createSeriesToggleHandler } from "@utilifeed/core/utils/createSeriesToggleHandler";
import { type RadiatorCurveBreakpoint } from "@core/apis/shared";

import { ChartLoader } from "../ChartLoader";

// Chart configuration utilities
const createTooltipConfig = (colors: { background: string; border: string }) => ({
  shared: false,
  useHTML: true,
  backgroundColor: colors.background,
  borderColor: colors.border,
  borderRadius: 5,
  shadow: true,
});

const createAxisConfig = (title: string) => ({
  title: { text: title },
  gridLineWidth: 1,
});

const createChartConfig = (height: number) => ({
  type: "scatter",
  zoomType: "xy",
  height,
});

const createPlotOptions = (hoverLineColor: string) => ({
  scatter: {
    marker: {
      radius: RADIATOR_CURVE_CONFIG.MARKER.RADIUS,
      states: {
        hover: {
          enabled: true,
          lineColor: hoverLineColor,
        },
      },
    },
    states: {
      hover: {
        marker: {
          enabled: false,
        },
      },
    },
  },
  spline: {
    lineWidth: RADIATOR_CURVE_CONFIG.SPLINE.LINE_WIDTH,
    states: {
      hover: {
        lineWidth: RADIATOR_CURVE_CONFIG.SPLINE.LINE_WIDTH + 1,
      },
    },
    marker: {
      enabled: false,
    },
  },
});

const createNoDataConfig = (textColor: string) => ({
  style: {
    fontWeight: "bold",
    fontSize: "15px",
    color: textColor,
  },
});

const RADIATOR_CURVE_CONFIG = {
  CHART: {
    HEIGHT: 480, // Reduced height to account for legend
  },
  MARKER: {
    RADIUS: 4,
    LINE_WIDTH: 0,
    FILL_OPACITY: 1,
    CURVE_LINE_WIDTH: 1,
  },
  SPLINE: {
    LINE_WIDTH: 3,
  },
} as const;

const CURVE_MARKER_LINE_COLOR = "#FFFFFF";

const transformRadiatorCurveData = (points: RadiatorCurveBreakpoint[]) =>
  points.map(({ outdoor_temperature: x, supply_temperature: y }) => ({ x, y }));

const isValidRadiatorPoint = (
  p: RadiatorCurveBreakpoint | undefined
): p is RadiatorCurveBreakpoint =>
  p && numbersOnly(p.outdoor_temperature) && numbersOnly(p.supply_temperature);

const sortByOutdoorTemp = (a: RadiatorCurveBreakpoint, b: RadiatorCurveBreakpoint) =>
  a.outdoor_temperature - b.outdoor_temperature;

const calculateAxisRange = (values: number[]): [number, number] | null => {
  if (!values || values.length === 0) return null;

  const validValues = values.filter((v) => v !== null && v !== undefined && !isNaN(v));
  if (validValues.length === 0) return null;

  const min = Math.min(...validValues);
  const max = Math.max(...validValues);
  const padding = (max - min) * 0.1; // 10% padding

  return [min - padding, max + padding];
};

const processRadiatorCurveData = (
  curvePoints: RadiatorCurveBreakpoint[],
  supplyDataPoints: RadiatorCurveBreakpoint[],
  isLoading: boolean
) => {
  if (isLoading) {
    return {
      validCurvePoints: [],
      validSupplyPoints: [],
      hasData: true,
      axisRanges: null,
    };
  }

  const curve = curvePoints?.filter(isValidRadiatorPoint).sort(sortByOutdoorTemp) || [];
  const supply = supplyDataPoints?.filter(isValidRadiatorPoint).sort(sortByOutdoorTemp) || [];
  const hasDataComputed = curve.length > 0 || supply.length > 0;

  let axisRangesComputed = null;
  if (hasDataComputed) {
    const allSupplyTemps = [
      ...curve.map((p) => p.supply_temperature),
      ...supply.map((p) => p.supply_temperature),
    ];
    const allOutdoorTemps = [
      ...curve.map((p) => p.outdoor_temperature),
      ...supply.map((p) => p.outdoor_temperature),
    ];

    const yRange = calculateAxisRange(allSupplyTemps);
    const xRange = calculateAxisRange(allOutdoorTemps);
    axisRangesComputed = yRange && xRange ? { y: yRange, x: xRange } : null;
  }

  return {
    validCurvePoints: curve,
    validSupplyPoints: supply,
    hasData: hasDataComputed,
    axisRanges: axisRangesComputed,
  };
};

class RadiatorCurveSeriesFactory {
  constructor(
    private graphColors: string[],
    private visibleSeries: SeriesVisibility,
    private handleLegendItemClick: () => void
  ) {}

  createSupplyDataSeries(validSupplyPoints: RadiatorCurveBreakpoint[]) {
    return {
      id: "supplyData",
      type: "scatter" as const,
      color: this.graphColors[0],
      visible: this.visibleSeries.supplyData,
      showInLegend: true,
      data: validSupplyPoints.length > 0 ? transformRadiatorCurveData(validSupplyPoints) : [],
      marker: {
        enabled: true,
        radius: RADIATOR_CURVE_CONFIG.MARKER.RADIUS,
        lineWidth: RADIATOR_CURVE_CONFIG.MARKER.LINE_WIDTH,
        fillOpacity: RADIATOR_CURVE_CONFIG.MARKER.FILL_OPACITY,
        symbol: "circle",
      },
      events: { legendItemClick: this.handleLegendItemClick },
    };
  }

  createCurveSeries(validCurvePoints: RadiatorCurveBreakpoint[]) {
    return {
      id: "curve",
      type: "spline" as const,
      color: this.graphColors[1],
      visible: this.visibleSeries.curve,
      showInLegend: true,
      data: validCurvePoints.length > 0 ? transformRadiatorCurveData(validCurvePoints) : [],
      marker: {
        enabled: true,
        radius: RADIATOR_CURVE_CONFIG.MARKER.RADIUS,
        lineWidth: RADIATOR_CURVE_CONFIG.MARKER.CURVE_LINE_WIDTH,
        lineColor: CURVE_MARKER_LINE_COLOR,
        symbol: "circle",
      },
      lineWidth: RADIATOR_CURVE_CONFIG.SPLINE.LINE_WIDTH,
      events: { legendItemClick: this.handleLegendItemClick },
    };
  }

  createAllSeries(
    validSupplyPoints: RadiatorCurveBreakpoint[],
    validCurvePoints: RadiatorCurveBreakpoint[]
  ) {
    return [
      this.createSupplyDataSeries(validSupplyPoints),
      this.createCurveSeries(validCurvePoints),
    ];
  }
}

type RadiatorCurveChartProps = {
  chartId?: string;
  curvePoints: RadiatorCurveBreakpoint[];
  supplyDataPoints?: RadiatorCurveBreakpoint[];
  isLoading?: boolean;
  showExport?: boolean;
  error?: string;
};

type SeriesVisibility = {
  curve: boolean;
  supplyData: boolean;
};

export const RadiatorCurveChart = withErrorBoundary(
  ({
    chartId = "radiator-curve",
    curvePoints,
    supplyDataPoints = [],
    isLoading,
    showExport = true,
    error,
  }: RadiatorCurveChartProps) => {
    const chartRef = useRef<HighchartsBaseRef>();
    const { t } = useTranslation(["extendView", "propai_properties", "_common"]);
    const theme = useTheme();
    const graphColors = useGraphColors();
    const [visibleSeries, setVisibleSeries] = useState<SeriesVisibility>({
      curve: true,
      supplyData: true,
    });
    const lastClickedLegend = useRef<string | undefined>();

    const seriesNames = useMemo(
      () => ({
        curve: t("text_radiator_curve"),
        supplyData: t("text_radiator_supply_temperature"),
      }),
      [t]
    );

    const { validCurvePoints, validSupplyPoints, hasData, axisRanges } = useMemo(
      () => processRadiatorCurveData(curvePoints, supplyDataPoints, isLoading),
      [curvePoints, supplyDataPoints, isLoading]
    );

    const handleLegendItemClick = useMemo(
      () => createSeriesToggleHandler(setVisibleSeries, lastClickedLegend),
      [setVisibleSeries, lastClickedLegend]
    );

    const series = useMemo(() => {
      const factory = new RadiatorCurveSeriesFactory(
        graphColors,
        visibleSeries,
        handleLegendItemClick
      );
      const factorySeries = factory.createAllSeries(validSupplyPoints, validCurvePoints);
      return factorySeries.map((series) => ({
        ...series,
        name: seriesNames[series.id as keyof typeof seriesNames],
      }));
    }, [graphColors, visibleSeries, handleLegendItemClick, validSupplyPoints, validCurvePoints]);

    const chartConfigs = useMemo(
      () => ({
        axis: createAxisConfig(theme),
        legend: {
          enabled: true,
          align: "left" as const,
          verticalAlign: "top" as const,
          layout: "horizontal" as const,
          itemStyle: {
            color: theme.palette.text.primary,
            fontSize: "12px",
          },
          symbolWidth: 16,
          symbolHeight: 12,
          margin: 20,
        },
        chart: createChartConfig(RADIATOR_CURVE_CONFIG.CHART.HEIGHT),
        plot: createPlotOptions(theme.palette.text.secondary),
        tooltip: createTooltipConfig({
          background: theme.palette.background.paper,
          border: theme.palette.divider,
        }),
        noData: createNoDataConfig(theme.palette.text.primary),
      }),
      [theme, chartId]
    );

    return (
      <Box
        sx={{
          width: "100%",
          minHeight: RADIATOR_CURVE_CONFIG.CHART.HEIGHT,
          position: "relative",
          overflow: "visible",
        }}
      >
        <ChartLoader isLoading={isLoading} error={error}>
          <GraphContainer hasData={hasData} data-testid={`chart-${chartId}-container`}>
            <HighchartsBase
              ref={chartRef}
              loading={isLoading}
              chart={chartConfigs.chart}
              legend={chartConfigs.legend}
              plotOptions={chartConfigs.plot}
              tooltip={{
                animation: false,
                formatter() {
                  const { x, y } = this;
                  return `<span style="color:${this.color}">●</span>
                          ${t("text_radiator_supply_temperature")}: <b>${formatNumberForLocale(y)}°C</b><br/>
                          ${t("text_outdoor_temperature")}: <b>${formatNumberForLocale(x)}°C</b>
                        `;
                },
              }}
              noData={chartConfigs.noData}
              xAxis={{
                title: { text: t("text_outdoor_temperature") },
                min: axisRanges?.x?.min,
                max: axisRanges?.x?.max,
                tickInterval: axisRanges?.x?.interval,
              }}
              yAxis={[
                {
                  title: { text: t("text_radiator_supply_temperature") },
                  min: axisRanges?.y?.min,
                  max: axisRanges?.y?.max,
                  tickInterval: axisRanges?.y?.interval,
                  gridLineWidth: 0,
                },
              ]}
              series={series}
              exporting={{
                enabled: showExport,
              }}
              showViewData={false}
            />
          </GraphContainer>
        </ChartLoader>
      </Box>
    );
  }
);
